import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuth } from '@/contexts/auth-context'

export function TeamInvitationPage() {
  const navigate = useNavigate()
  const [teamEmails, setTeamEmails] = useState('')
  const [sendInvites, setSendInvites] = useState(true)
  const { user, loading } = useAuth()

  useEffect(() => {
    if (loading) return

    if (!user) {
      navigate('/login', { replace: true })
      return
    }

    if (user.onboardingCompleted) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, loading, navigate])

  if (loading || !user || user.onboardingCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const handleSubmit = () => {
    const teamData = {
      emails: teamEmails.split(/[\n,]/).map((email) => email.trim()).filter(Boolean),
      sendInvites,
    }
    console.log('Team invitation data:', teamData)
    navigate('/onboarding/complete')
  }

  const handleSkip = () => {
    navigate('/onboarding/complete')
  }

  const handleBack = () => {
    navigate('/onboarding')
  }

  return (
    <div>
      <div className="text-center mb-8">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold mb-4">
          2
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Invite your team</h2>
        <p className="text-muted-foreground">Add team members to get started with feedback cycles</p>
      </div>

      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <Label htmlFor="teamEmails" className="mb-2 block">
            Team Member Emails
          </Label>
          <Textarea
            id="teamEmails"
            value={teamEmails}
            onChange={(e) => setTeamEmails(e.target.value)}
            placeholder={"Enter email addresses separated by commas or new lines\n\nExample:\njohn@company.com\nsarah@company.com\nmike@company.com"}
            rows={5}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground mt-2">Don&apos;t worry, you can always add more team members later</p>
        </div>

        <Card
          className={`border-2 p-4 cursor-pointer transition-all mb-8 ${sendInvites ? 'border-primary bg-primary-50' : 'border-border'}`}
          onClick={() => setSendInvites((prev) => !prev)}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary-100 text-primary-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-mail"
              >
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <h4 className="font-semibold text-foreground">Send invitation emails immediately</h4>
          </div>
          <p className="text-muted-foreground text-sm ml-11">
            Team members will receive an email invitation to join your organization
          </p>
          <div className="flex items-center mt-2">
            <Checkbox
              id="sendInvites"
              checked={sendInvites}
              onCheckedChange={(checked) => setSendInvites(!!checked)}
              className="data-[state=checked]:bg-foreground data-[state=checked]:text-white"
            />
          </div>
        </Card>

        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={handleBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Back
          </Button>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Skip for now
            </button>
            <Button onClick={handleSubmit}>
              Continue
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ml-2"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamInvitationPage
